import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Paper, Stack, Button,
  IconButton, CircularProgress, Alert,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { issuerSchema } from '../../validation/invoiceSchema';
import InvoiceIssuerService from '../../services/InvoiceIssuerService';
import IssuerProfileForm from '../../components/invoice/IssuerProfileForm';
import apiClient from '../../apiClient';

const issuerService = new InvoiceIssuerService();

const IssuerProfile = () => {
  const navigate = useNavigate();
  const [currentIssuer, setCurrentIssuer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(issuerSchema),
    defaultValues: {
      name: '',
      businessName: '',
      abn: '',
      email: '',
      phone: '',
      address: { street: '', city: '', state: '', postcode: '', country: 'Australia' },
      bankDetails: { bankName: '', bsb: '', accountNumber: '', accountName: '' },
      paymentTerms: '',
    },
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await issuerService.getAll();
      if (data.length > 0) {
        const defaultIssuer = data.find((i) => i.isDefault) || data[0];
        setCurrentIssuer(defaultIssuer);
        reset({
          name: defaultIssuer.name || '',
          businessName: defaultIssuer.businessName || '',
          abn: defaultIssuer.abn || '',
          email: defaultIssuer.email || '',
          phone: defaultIssuer.phone || '',
          address: defaultIssuer.address || { street: '', city: '', state: '', postcode: '', country: 'Australia' },
          bankDetails: defaultIssuer.bankDetails || { bankName: '', bsb: '', accountNumber: '', accountName: '' },
          paymentTerms: defaultIssuer.paymentTerms || '',
        });
        if (defaultIssuer.logoUrl) {
          setLogoPreview(defaultIssuer.logoUrl);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onSubmit = async (data) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (currentIssuer) {
        await issuerService.update(currentIssuer.id, data);
      } else {
        const created = await issuerService.create(data);
        setCurrentIssuer(created);
      }
      navigate('/invoice');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be smaller than 2MB');
      return;
    }

    try {
      let issuerId = currentIssuer?.id;
      if (!issuerId) {
        // Create the issuer first
        const formData = {
          name: 'My Business',
          email: 'update@email.com',
        };
        const created = await issuerService.create(formData);
        issuerId = created.id;
        setCurrentIssuer(created);
      }

      await issuerService.uploadLogo(issuerId, file);
      setLogoPreview(URL.createObjectURL(file));
      setSuccess('Logo uploaded successfully');
    } catch (err) {
      setError(err.message || 'Failed to upload logo');
    }
  };

  const handleCopyFromProfile = async () => {
    setError(null);
    try {
      let profileData = null;

      // Try company profile first
      try {
        const companyProfile = await apiClient.get('/company-profiles/me');
        if (companyProfile && (companyProfile.companyName || companyProfile.email)) {
          profileData = {
            name: companyProfile.companyName || '',
            businessName: companyProfile.companyName || '',
            abn: companyProfile.abn || '',
            email: companyProfile.email || '',
            phone: companyProfile.phone || '',
            address: {
              street: '',
              city: companyProfile.location || '',
              state: '',
              postcode: '',
              country: 'Australia',
            },
          };
        }
      } catch (_) { /* no company profile */ }

      // Fall back to jobseeker profile
      if (!profileData) {
        try {
          const jsProfile = await apiClient.get('/job-seeker-profiles/me');
          const basicProfile = await apiClient.get('/profiles/me');
          if (jsProfile || basicProfile) {
            const firstName = basicProfile?.firstName || '';
            const lastName = basicProfile?.lastName || '';
            profileData = {
              name: [firstName, lastName].filter(Boolean).join(' ') || '',
              businessName: '',
              abn: jsProfile?.abnNumber || '',
              email: basicProfile?.email || '',
              phone: basicProfile?.phone || '',
              address: {
                street: '',
                city: jsProfile?.suburb || jsProfile?.location || '',
                state: jsProfile?.state || '',
                postcode: jsProfile?.postcode || '',
                country: 'Australia',
              },
            };
          }
        } catch (_) { /* no jobseeker profile */ }
      }

      if (profileData) {
        const currentValues = control._formValues;
        reset({
          ...currentValues,
          name: profileData.name || currentValues.name || '',
          businessName: profileData.businessName || currentValues.businessName || '',
          abn: profileData.abn || currentValues.abn || '',
          email: profileData.email || currentValues.email || '',
          phone: profileData.phone || currentValues.phone || '',
          address: {
            ...currentValues.address,
            city: profileData.address.city || currentValues.address?.city || '',
            state: profileData.address.state || currentValues.address?.state || '',
            postcode: profileData.address.postcode || currentValues.address?.postcode || '',
            country: 'Australia',
          },
        });
        setSuccess('Profile data copied successfully');
      } else {
        setSuccess('No existing profile found. Please fill in your details manually.');
      }
    } catch (err) {
      setError('Failed to load profile data');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Box display="flex" justifyContent="center"><CircularProgress /></Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/invoice')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>Billing Profile</Typography>
            <Typography variant="body2" color="text.secondary">
              Your business details for invoices
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

        <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={3}>
            <IssuerProfileForm
              control={control}
              errors={errors}
              logoPreview={logoPreview}
              onLogoUpload={handleLogoUpload}
              onCopyProfile={handleCopyFromProfile}
            />

            {/* Save */}
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={() => navigate('/invoice')}>Cancel</Button>
              <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={saving}>
                {saving ? 'Saving…' : 'Save Profile'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

export default IssuerProfile;
